// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\PartsCatalog.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Context } from "../store/appContext";
import getApiBaseUrl from "../utils/api";
import "../styles/layout.css";
import "../styles/utilities.css";
import "../styles/forms.css";
import "../styles/catalog.css";

const currencyFormatter = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));
const formatNumber = (value) => numberFormatter.format(Number(value || 0));
const PAGE_SIZE = 10;
const API_URL = getApiBaseUrl();

const getAverageUnitPrice = (row) =>
  row?.average_unit_price ?? row?.avg_unit_price ?? row?.average_price ?? null;

const getSupplierCount = (row) => {
  const value = row?.supplier_count ?? row?.distinct_suppliers;

  if (value === null || value === undefined || value === "") {
    return null;
  }

  return Number(value || 0);
};

const getSortIndicator = (sortConfig, key) => {
  if (sortConfig.key !== key) {
    return "";
  }

  return sortConfig.direction === "asc" ? " ▲" : " ▼";
};

const PartsCatalog = () => {
  const { store, actions } = useContext(Context);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: PAGE_SIZE,
    total: 0,
    pages: 0,
  });
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const token = store.token;

  useEffect(() => {
    const controller = new AbortController();

    const loadParts = async () => {
      if (!token) {
        setRows([]);
        setSummary(null);
        return;
      }

      const params = new URLSearchParams({
        page: String(currentPage),
        per_page: String(PAGE_SIZE),
      });
      const normalizedSearch = appliedSearch.trim();
      if (normalizedSearch) params.set("search", normalizedSearch);
      if (sortConfig.key) {
        params.set("sort_by", sortConfig.key);
        params.set("sort_order", sortConfig.direction);
      }

      setIsLoading(true);
      setErrorMessage("");

      try {
        const response = await fetch(
          `${API_URL}/api/catalog/parts?${params.toString()}`,
          {
            headers: { Authorization: "Bearer " + token },
            signal: controller.signal,
          },
        );
        const payload = await response.json().catch(() => null);

        if (response.status === 401) {
          await actions.handleApiError(response);
          return;
        }

        if (!response.ok || !payload?.success) {
          setRows([]);
          setSummary(null);
          setErrorMessage(payload?.error || "No se pudo cargar el catálogo");
          return;
        }

        setRows(Array.isArray(payload.data?.rows) ? payload.data.rows : []);
        setPagination((current) => ({
          ...current,
          ...(payload.data?.pagination || {}),
        }));
        setSummary(payload.data?.summary || null);
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Parts catalog load error:", error);
        setRows([]);
        setSummary(null);
        setErrorMessage("No se pudo cargar el catálogo");
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadParts();

    return () => controller.abort();
  }, [appliedSearch, currentPage, sortConfig, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setAppliedSearch(search.trim());
    setCurrentPage(1);
  };

  const handleClear = async () => {
    setSearch("");
    setAppliedSearch("");
    setCurrentPage(1);
  };

  const handleOpenDetail = (materialId) => {
    if (!materialId) {
      return;
    }

    navigate(`/parts/${encodeURIComponent(materialId)}`);
  };

  const totalRows = Number(pagination.total || 0);
  const totalPages = Math.max(1, Number(pagination.pages || 1));
  const firstRowIndex = (currentPage - 1) * PAGE_SIZE;
  const lastRowIndex = Math.min(firstRowIndex + rows.length, totalRows);
  const hasAverageUnitPrice = rows.some(
    (row) => getAverageUnitPrice(row) !== null && getAverageUnitPrice(row) !== undefined,
  );
  const paginatedRows = rows;
  const topPart = summary?.top_part || rows[0] || null;
  const hasSupplierCounts = rows.some((row) => getSupplierCount(row) !== null);

  const kpis = useMemo(
    () =>
      rows.reduce(
        (accumulator, row) => ({
          totalParts: accumulator.totalParts + 1,
          totalSpend: accumulator.totalSpend + Number(row.total_spend || 0),
          purchaseLines:
            accumulator.purchaseLines + Number(row.purchase_lines || 0),
          supplierCount:
            accumulator.supplierCount + Number(getSupplierCount(row) || 0),
        }),
        {
          totalParts: 0,
          totalSpend: 0,
          purchaseLines: 0,
          supplierCount: 0,
        }
      ),
    [rows]
  );
  const displayKpis = summary
    ? {
        totalParts: summary.total_parts,
        totalSpend: summary.total_spend,
        purchaseLines: summary.purchase_lines,
        supplierCount: summary.supplier_count,
      }
    : kpis;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSort = async (key) => {
    const nextSortConfig = {
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc",
    };
    setSortConfig(nextSortConfig);
    setCurrentPage(1);
  };

  const renderSortableHeader = (key, label, className = "") => (
    <th className={className}>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none text-reset fw-semibold"
        onClick={() => handleSort(key)}
        disabled={isLoading}
      >
        {label}
        {getSortIndicator(sortConfig, key)}
      </button>
    </th>
  );

  return (
    <div className="container mt-5 catalog-page">
      <div className="page-header catalog-header">
        <div className="catalog-title-block">
          <span className="catalog-title-icon" aria-hidden="true">
            ⚙️
          </span>
          <div>
            <h1 className="page-title">Catálogo de repuestos</h1>
            <p className="page-subtitle">
              Ranking de repuestos según compras registradas en almacén.
            </p>
          </div>
        </div>
      </div>

      <section className="catalog-kpi-grid" aria-label="Indicadores de repuestos">
        <article className="catalog-kpi-card catalog-kpi-card-parts">
          <span className="catalog-kpi-icon" aria-hidden="true">
            ⚙️
          </span>
          <div>
            <span className="catalog-kpi-label">Total repuestos</span>
            <strong>{formatNumber(displayKpis.totalParts)}</strong>
          </div>
        </article>
        <article className="catalog-kpi-card catalog-kpi-card-spend">
          <span className="catalog-kpi-icon" aria-hidden="true">
            💶
          </span>
          <div>
            <span className="catalog-kpi-label">Gasto total</span>
            <strong>{formatCurrency(displayKpis.totalSpend)}</strong>
          </div>
        </article>
        <article className="catalog-kpi-card catalog-kpi-card-lines">
          <span className="catalog-kpi-icon" aria-hidden="true">
            📄
          </span>
          <div>
            <span className="catalog-kpi-label">Líneas de compra</span>
            <strong>{formatNumber(displayKpis.purchaseLines)}</strong>
          </div>
        </article>
        <article className="catalog-kpi-card catalog-kpi-card-suppliers">
          <span className="catalog-kpi-icon" aria-hidden="true">
            🏢
          </span>
          <div>
            <span className="catalog-kpi-label">Proveedores acumulados</span>
            <strong>{hasSupplierCounts ? formatNumber(displayKpis.supplierCount) : "-"}</strong>
          </div>
        </article>
        <article className="catalog-kpi-card catalog-kpi-card-leader">
          <span className="catalog-kpi-icon" aria-hidden="true">
            🏆
          </span>
          <div>
            <span className="catalog-kpi-label">Repuesto principal</span>
            <strong>{topPart?.material_id || "-"}</strong>
          </div>
        </article>
      </section>

      {topPart && (
        <section className="catalog-top-supplier-card">
          <div className="catalog-top-supplier-main">
            <span className="catalog-top-supplier-icon" aria-hidden="true">
              🥇
            </span>
            <div>
              <span className="catalog-eyebrow">Top repuesto</span>
              <h2>{topPart.material_id || "Repuesto sin código"}</h2>
              <p>{topPart.description || "Sin descripción disponible"}</p>
            </div>
          </div>
          <div className="catalog-top-supplier-metrics">
            <span>
              <strong>{formatCurrency(topPart.total_spend)}</strong>
              Gasto total
            </span>
            <span>
              <strong>{formatNumber(topPart.purchase_lines)}</strong>
              Líneas
            </span>
            <button
              type="button"
              className="catalog-detail-button catalog-detail-button-highlight"
              onClick={() => handleOpenDetail(topPart.material_id)}
              disabled={!topPart.material_id}
            >
              Ver detalle
            </button>
          </div>
        </section>
      )}

      <form className="catalog-filters" onSubmit={handleSubmit}>
        <div className="catalog-filter-group">
          <label htmlFor="parts_catalog_search">
            Buscar por código o descripción
          </label>
          <div className="catalog-search-input-wrap">
            <span aria-hidden="true">🔎</span>
            <input
              id="parts_catalog_search"
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Código o descripción del repuesto"
            />
          </div>
        </div>

        <div className="catalog-filter-actions">
          <button className="btn btn-primary" type="submit" disabled={isLoading}>
            Buscar
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={handleClear}
            disabled={isLoading}
          >
            Limpiar
          </button>
        </div>
      </form>

      <section className="catalog-card">
        <div className="catalog-section-head">
          <div>
            <h2>Ranking por gasto</h2>
            <p>
              Repuestos ordenados por volumen de compra acumulado.
            </p>
          </div>
          <span className="catalog-table-count">
            {totalRows > 0
              ? `${formatNumber(firstRowIndex + 1)}-${formatNumber(lastRowIndex)} de ${formatNumber(totalRows)}`
              : `0 de ${formatNumber(totalRows)}`}
          </span>
        </div>

        {isLoading && (
          <div className="catalog-state catalog-state-loading catalog-state-inline">
            Cargando catálogo de repuestos...
          </div>
        )}

        {!isLoading && errorMessage && (
          <div className="catalog-state catalog-state-error catalog-state-inline">
            {errorMessage}
          </div>
        )}

        {!isLoading && !errorMessage && rows.length === 0 && (
          <div className="catalog-state catalog-state-empty catalog-state-inline">
            No hay repuestos que coincidan con la búsqueda actual.
          </div>
        )}

        {!errorMessage && rows.length > 0 && (
          <div className="table-responsive catalog-table-scroll parts-catalog-table-wrap">
            <table className="table table-compact mb-0 parts-catalog-table">
              <thead>
                <tr>
                  <th>Ranking</th>
                  {renderSortableHeader("material_id", "Material")}
                  {renderSortableHeader("description", "Descripción")}
                  {renderSortableHeader("total_spend", "Gasto total", "text-end")}
                  {renderSortableHeader("total_quantity", "Cantidad total", "text-end")}
                  <th className="text-end">Líneas de compra</th>
                  {renderSortableHeader("suppliers", "Proveedores", "text-end")}
                  {hasAverageUnitPrice &&
                    renderSortableHeader("average_unit_price", "Precio medio", "text-end")}
                  <th className="text-end">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row, index) => {
                  const rank = firstRowIndex + index + 1;
                  const supplierCount = getSupplierCount(row);

                  return (
                    <tr key={row.material_id || `${row.description}-${rank}`}>
                      <td>
                        <span className={`catalog-rank catalog-rank-${rank}`}>
                          #{rank}
                        </span>
                      </td>
                      <td className="cell-strong">
                        {row.material_id ? (
                          <button
                            type="button"
                            className="catalog-link-button"
                            onClick={() => handleOpenDetail(row.material_id)}
                          >
                            {row.material_id}
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{row.description || "-"}</td>
                      <td className="text-end">
                        <span className="catalog-chip catalog-chip-spend">
                          {formatCurrency(row.total_spend)}
                        </span>
                      </td>
                      <td className="text-end">{formatNumber(row.total_quantity)}</td>
                      <td className="text-end">
                        <span className="catalog-chip catalog-chip-lines">
                          {formatNumber(row.purchase_lines)}
                        </span>
                      </td>
                      <td className="text-end">
                        <span className="catalog-chip catalog-chip-parts">
                          {supplierCount === null ? "-" : formatNumber(supplierCount)}
                        </span>
                      </td>
                      {hasAverageUnitPrice && (
                        <td className="text-end">
                          {formatCurrency(getAverageUnitPrice(row))}
                        </td>
                      )}
                      <td className="text-end">
                        <button
                          type="button"
                          className="catalog-detail-button"
                          onClick={() => handleOpenDetail(row.material_id)}
                          disabled={!row.material_id}
                        >
                          Ver detalle
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="catalog-pagination">
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            disabled={isLoading || currentPage === 1}
          >
            Anterior
          </button>
          <span className="catalog-pagination-info">
            Página <strong>{formatNumber(currentPage)}</strong> de{" "}
            <strong>{formatNumber(totalPages)}</strong>
          </span>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() =>
              setCurrentPage((page) => Math.min(totalPages, page + 1))
            }
            disabled={isLoading || currentPage === totalPages}
          >
            Siguiente
          </button>
        </div>
      </section>
    </div>
  );
};

export default PartsCatalog;
